import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import { css } from "glamor";
import QuestionInteractive from "./QuestionInteractive";
import QuestionV2 from "../QuestionV2";
import InteractiveLayout from "./InteractiveLayout";
import InteractiveContextColumn from "./InteractiveContextColumn";
import { InteractiveNavFooterContext } from "./InteractiveNavFooterContext";
import { spacing } from "../../library/theme";
import { getMock } from "../../../selectors/mockSelectors";
import {
  buildAnswersByPrompt,
  buildConversationPath,
  getStartPrompt,
  sortPromptsByPosition,
  toAlternativeMap,
  toPromptMap,
} from "./questionInteractiveUtils";

/**
 * QuestionInteractiveWrapper
 *
 * Wraps the interactive question in a two-column layout.
 * - Flow entry (isFlow): left = context, right = QuestionInteractive (flow).
 * - Stem entry of hybrid interactive: left = conversation recap, right = QuestionV2 stem.
 *
 * Handled at this level (not inside Question.js) so the recap entries and
 * revealResult can be derived together from Redux once.
 */
const navFooterClass = css({
  marginTop: spacing.m,
});

function QuestionInteractiveWrapper(props) {
  const { question, isFlow, recapEntries, revealResult, currentEntry } = props;
  const [navFooterEl, setNavFooterEl] = useState(null);

  const rightColumn = isFlow ? (
    <QuestionInteractive {...props} />
  ) : (
    <QuestionV2
      {...props}
      currentStemPosition={currentEntry?.stem?.position}
    />
  );

  const leftColumn = isFlow ? (
    <InteractiveContextColumn question={question} mode="context" />
  ) : (
    <InteractiveContextColumn
      question={question}
      mode="recap"
      recapEntries={recapEntries}
      revealResult={revealResult}
    />
  );

  return (
    <InteractiveNavFooterContext.Provider value={navFooterEl}>
      <InteractiveLayout leftColumn={leftColumn} rightColumn={rightColumn} />
      <div ref={setNavFooterEl} className={navFooterClass} />
    </InteractiveNavFooterContext.Provider>
  );
}

function ConnectedWrapper(props) {
  const { question, isFlow, allAnswers, revealResult } = props;

  const recapEntries = useMemo(() => {
    if (isFlow) return [];
    const prompts = sortPromptsByPosition(question?.meta?.questionPrompts || []);
    const promptsById = toPromptMap(prompts);
    const startPrompt = getStartPrompt(prompts);
    const answersByPrompt = buildAnswersByPrompt(allAnswers, question?.id);
    const path = buildConversationPath({
      startPrompt,
      promptsById,
      answersByPrompt,
    });
    return path
      .filter((entry) => entry.answer != null)
      .map((entry) => {
        const altMap = toAlternativeMap(entry.prompt);
        const selectedAlternative =
          altMap[entry.answer?.questionPromptAlternativeId] || null;
        return {
          prompt: entry.prompt,
          answer: entry.answer,
          selectedAlternative,
          isCorrect: entry.answer?.correct === true,
        };
      });
  }, [isFlow, question, allAnswers]);

  return (
    <QuestionInteractiveWrapper
      {...props}
      recapEntries={recapEntries}
      revealResult={revealResult}
    />
  );
}

function mapStateToProps(state, ownProps) {
  const { question, lesson } = ownProps;
  const mock = getMock(state);
  const mockId = mock?.id;
  const isFinished = !!mock?.finishedAt;
  const isExam = !lesson && !!mock?.exam;
  const revealResult = !!ownProps.readOnly || !isExam || isFinished;

  const answers = Object.values(state.entities?.answers || {});
  const allAnswers = answers.filter((a) => {
    if (!a) return false;
    if (Number(a.questionId) !== Number(question?.id)) return false;
    if (lesson?.id) return Number(a.lessonId) === Number(lesson.id);
    if (mockId == null) return false;
    return Number(a.mockId) === Number(mockId);
  });

  return {
    allAnswers,
    revealResult,
  };
}

export default connect(mapStateToProps)(ConnectedWrapper);
